export const CANONICAL_DRINK_CATEGORIES = [
  "Bier",
  "Schnaps",
  "Wein",
  "Longdrinks",
  "Cocktails",
  "Nicht Alkoholisch",
] as const;

export type CanonicalDrinkCategory =
  (typeof CANONICAL_DRINK_CATEGORIES)[number];

const CATEGORY_ALIASES: Record<string, CanonicalDrinkCategory> = {
  bier: "Bier",
  beer: "Bier",
  pils: "Bier",
  lager: "Bier",
  weizen: "Bier",
  helles: "Bier",
  altbier: "Bier",
  kolsch: "Bier",
  kolschbier: "Bier",
  koelsch: "Bier",
  koelschbier: "Bier",
  schnaps: "Schnaps",
  spirit: "Schnaps",
  spirits: "Schnaps",
  spirituose: "Schnaps",
  spirituosen: "Schnaps",
  shots: "Schnaps",
  wein: "Wein",
  wine: "Wein",
  rotwein: "Wein",
  weisswein: "Wein",
  weißwein: "Wein",
  rosewein: "Wein",
  rose: "Wein",
  roseweinchen: "Wein",
  roseweine: "Wein",
  rosé: "Wein",
  roséwein: "Wein",
  sekt: "Wein",
  prosecco: "Wein",
  champagner: "Wein",
  cocktail: "Cocktails",
  cocktails: "Cocktails",
  longdrink: "Longdrinks",
  longdrinks: "Longdrinks",
  nichtalkoholisch: "Nicht Alkoholisch",
  nichtalkoholisches: "Nicht Alkoholisch",
  nichtalkoholische: "Nicht Alkoholisch",
  nichtalkoholischer: "Nicht Alkoholisch",
  nonalcoholic: "Nicht Alkoholisch",
  non_alcoholic: "Nicht Alkoholisch",
  softdrink: "Nicht Alkoholisch",
  softdrinks: "Nicht Alkoholisch",
  softdrinke: "Nicht Alkoholisch",
  soft_drink: "Nicht Alkoholisch",
  soft_drinks: "Nicht Alkoholisch",
  wasser: "Nicht Alkoholisch",
  limo: "Nicht Alkoholisch",
  limonade: "Nicht Alkoholisch",
  saft: "Nicht Alkoholisch",
  hotbeverage: "Nicht Alkoholisch",
  hot_beverage: "Nicht Alkoholisch",
  other: "Nicht Alkoholisch",
  sonstiges: "Nicht Alkoholisch",
  sonstige: "Nicht Alkoholisch",
};

const UNKNOWN_LOCATION_TOKENS = new Set([
  "",
  "unbekannt",
  "unknown",
  "n/a",
  "na",
  "null",
  "undefined",
  "-",
]);

const parseCityFromString = (value: string): string | null => {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return null;
  }

  const zipCityMatch = normalizedValue.match(
    /\b\d{4,5}\s*,?\s*([A-Za-zÄÖÜäöüß][A-Za-zÄÖÜäöüß .'-]*)$/,
  );
  if (zipCityMatch?.[1]) {
    return zipCityMatch[1].trim().replace(/[.,\s]+$/g, "");
  }

  const parts = normalizedValue
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    const lastPart = parts[parts.length - 1]?.replace(/[.,\s]+$/g, "") ?? "";
    if (lastPart && !/\d/.test(lastPart) && !UNKNOWN_LOCATION_TOKENS.has(lastPart.toLowerCase())) {
      return lastPart;
    }
  }

  if (!/\d/.test(normalizedValue) && normalizedValue.length <= 80) {
    return normalizedValue.replace(/[.,\s]+$/g, "");
  }

  return null;
};

export const extractCityFromAddress = (address: unknown): string => {
  if (!address) {
    return "";
  }

  if (typeof address === "string") {
    return parseCityFromString(address) ?? "";
  }

  if (typeof address === "object") {
    const data = address as Record<string, unknown>;
    return getDisplayCity(data.city, data.town, data.village, data.municipality);
  }

  return "";
};

const toAsciiKey = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ß/g, "ss");

export const normalizeSearchValue = (value: unknown): string =>
  toAsciiKey(String(value ?? ""))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();

export const normalizeDrinkCategory = (
  raw: unknown,
): CanonicalDrinkCategory => {
  const rawValue = String(raw ?? "").trim();
  const normalized = normalizeSearchValue(rawValue);
  return CATEGORY_ALIASES[normalized] ?? "Nicht Alkoholisch";
};

export const sortDrinkCategories = (categories: string[]): string[] =>
  [...categories].sort((left, right) => {
    const leftIndex = CANONICAL_DRINK_CATEGORIES.indexOf(
      normalizeDrinkCategory(left),
    );
    const rightIndex = CANONICAL_DRINK_CATEGORIES.indexOf(
      normalizeDrinkCategory(right),
    );

    if (leftIndex !== rightIndex) {
      return leftIndex - rightIndex;
    }

    return left.localeCompare(right, "de");
  });

export const getDrinkBrand = (source: Record<string, unknown>): string => {
  const candidates = [
    source.brand,
    source.brandName,
    source.brewery,
    source.manufacturer,
    source.producer,
    source.maker,
  ];

  for (const candidate of candidates) {
    const value = String(candidate ?? "").trim();
    if (value.length > 0) {
      return value;
    }
  }

  return "";
};

const normalizeNumericVolume = (value: number): number | null => {
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }

  if (value < 10) {
    return Math.round(value * 1000);
  }

  return Math.round(value);
};

const parseVolumeString = (value: string): number | null => {
  const normalized = value.trim().toLowerCase().replace(",", ".");
  const match = normalized.match(/(\d+(?:\.\d+)?)\s*(ml|cl|l)?/);

  if (!match) {
    return null;
  }

  const amount = Number(match[1]);
  const unit = match[2] ?? "";

  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  if (unit === "ml") {
    return Math.round(amount);
  }

  if (unit === "cl") {
    return Math.round(amount * 10);
  }

  if (unit === "l" || amount < 10) {
    return Math.round(amount * 1000);
  }

  return Math.round(amount);
};

export const parseVolumeMl = (value: unknown): number | null => {
  if (typeof value === "number") {
    return normalizeNumericVolume(value);
  }

  if (typeof value === "string") {
    return parseVolumeString(value);
  }

  return null;
};

export const extractDrinkVolumeMl = (
  source: Record<string, unknown>,
): number | null => {
  const candidates = [
    source.volumeML,
    source.volumeMl,
    source.sizeML,
    source.sizeMl,
    source.amountMl,
    source.volume,
  ];

  for (const candidate of candidates) {
    const parsed = parseVolumeMl(candidate);
    if (parsed != null) {
      return parsed;
    }
  }

  return null;
};

export const formatVolumeLabel = (volumeMl: number | null | undefined): string => {
  if (!volumeMl || volumeMl <= 0) {
    return "";
  }

  if (volumeMl < 100) {
    return `${Math.round(volumeMl / 10)} cl`;
  }

  return `${(volumeMl / 1000).toFixed(1).replace(".", ",")} l`;
};

export const getDisplayCity = (...values: Array<unknown>): string => {
  for (const value of values) {
    const candidate =
      typeof value === "string"
        ? extractCityFromAddress(value) || value.trim()
        : typeof value === "object" && value !== null
          ? extractCityFromAddress(value)
          : String(value ?? "").trim();

    if (!UNKNOWN_LOCATION_TOKENS.has(candidate.toLowerCase())) {
      return candidate;
    }
  }

  return "Unbekannt";
};

export const getDisplayBarName = (...values: Array<unknown>): string => {
  for (const value of values) {
    const candidate = String(value ?? "").trim();
    if (candidate.length > 0) {
      return candidate;
    }
  }

  return "Unbekannte Bar";
};
